import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

// As peças dos cinco módulos do HUB DP.
//
// ---------------------------------------------------------------------------
// A primeira versão era feia, e o motivo tem nome
// ---------------------------------------------------------------------------
// Victor viu e disse que não agradou. Ao olhar as capturas, o diagnóstico foi
// direto: **eu não usei nenhuma das peças bonitas que o hub já tem.** O resultado
// do holerite era uma pilha de cinco caixas do mesmo tamanho, com a mesma borda e
// o mesmo peso — PROVENTOS, DESCONTOS, LÍQUIDO, COMPARAÇÃO, DEPÓSITO —, e o
// número que o aluno quer ver estava perdido no meio dela. Nenhuma hierarquia, e
// nenhuma imagem: só listas de números numa ferramenta cujo valor é ENSINAR.
//
// O hub tem `painel-navy` (o painel azul quadriculado), `tv-card` (o cartão de
// vidro sobre navy) e `rise` (a entrada em escada), e usa os três no hero da
// inicial, no card de Aulas e na Faculdade inteira. "Navy é território" está no
// CLAUDE.md — e uma ferramenta de cinco módulos é exatamente o tipo de tela que
// pede território.
//
// O que este arquivo passou a ter, e antes não tinha:
//
//  · `HeroModulo` — painel navy com o NÚMERO HERÓI num cartão de vidro, vivo
//    enquanto o aluno digita. Um número grande, e não cinco médios;
//  · `Cascata` — a composição em barra proporcional. Dado real, e é o que faz o
//    aluno VER que o desconto é uma fatia pequena do bruto;
//  · `Regua` — as faixas da tabela em escala, com o marcador onde o salário caiu.
//    É a peça mais didática do conjunto: substitui uma tabela por uma imagem que
//    responde "por que caí nesta alíquota?";
//  · `Comparador` — dois cartões lado a lado com o vencedor coroado, em vez de
//    duas linhas de lista;
//  · `Contracheque` — proventos e descontos em DUAS COLUNAS, que é a forma que o
//    contador reconhece de imediato.
//
// ---------------------------------------------------------------------------
// Sobre navy, cor semântica é armadilha
// ---------------------------------------------------------------------------
// Dentro de `painel-navy` e `tv-card` o fundo é escuro NOS DOIS TEMAS, então
// `text-tinta` clareia no escuro e vira branco sobre branco — foi o que aconteceu
// com os cards em escada do login. Ali só entram `navy-ink` (#f1f1f2) e
// `navy-mute` (#aab4c8), e nunca `cinza` (#727376), que reprova em contraste sobre
// #102850. A prova de tela mede a luminância dos dois.
//
// ---------------------------------------------------------------------------
// O que NÃO está aqui, e é de propósito
// ---------------------------------------------------------------------------
// `Cartao`, `Campo`, `Dica`, `Caixa`, `Resumo` e `CAMPO_SIM` vêm de
// `SimuladorMeiUi.tsx` — reusados, não copiados. Duplicá-las é como uma passa a
// discordar da outra. Se o nome do arquivo de origem incomodar, o caminho é
// RENOMEAR aquele arquivo, nunca copiar o conteúdo para cá.
//
// ---------------------------------------------------------------------------
// As abas são SUB-ROTAS, não estado
// ---------------------------------------------------------------------------
// Na origem eram `useState` num componente do Radix. Aqui são rotas
// (`/ferramenta/hub-dp/holerite/referencia`): o aluno volta pelo botão do
// navegador, compartilha o link de uma aba, e recarregar não o joga para a
// primeira.
export { Cartao, Campo, Dica, Caixa, Resumo, CAMPO_SIM } from "./SimuladorMeiUi";

// ---------------------------------------------------------------------------
// O hero de cada módulo
// ---------------------------------------------------------------------------

/** O número herói do módulo: o que o aluno abriu a tela para ver. */
export interface Heroi {
  rotulo: string;
  valor: string;
  /** Uma linha de contexto sob o número — a conta que o explica. */
  detalhe?: string;
}

/**
 * O cabeçalho de um módulo: painel navy com a trilha, o título e o número herói.
 *
 * O número mora num `tv-card` à direita e é VIVO — muda enquanto o aluno digita.
 * Sem ele, o cartão diz o que falta preencher, em vez de mostrar zero: `R$ 0,00`
 * num holerite parece um resultado, e é ausência de dado.
 *
 * A trilha volta ao índice do HUB DP, não à raiz do hub: quem entrou no Holerite
 * quer voltar aos cinco módulos, e o Topo já leva à inicial.
 */
export function HeroModulo({
  titulo,
  subtitulo,
  heroi,
  vazio,
}: {
  titulo: string;
  subtitulo: string;
  heroi?: Heroi | null;
  /** O que dizer no cartão enquanto não há número. */
  vazio?: string;
}) {
  return (
    <header className="painel-navy rounded-cf-lg px-5 py-6 sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <nav aria-label="Trilha" className="text-xs tracking-[0.14em] text-navy-mute uppercase">
            <Link to="/ferramenta/hub-dp" className="underline decoration-navy-mute/40 hover:text-navy-ink">
              HUB DP
            </Link>
            <span aria-hidden="true" className="mx-1.5">
              ›
            </span>
            <span className="text-navy-ink">{titulo}</span>
          </nav>
          <h1 className="rise mt-2 font-display text-3xl font-bold text-navy-ink sm:text-4xl">
            {titulo}
          </h1>
          <p
            className="rise mt-2 max-w-xl text-sm leading-relaxed text-navy-mute"
            style={{ "--i": 1 } as React.CSSProperties}
          >
            {subtitulo}
          </p>
        </div>

        <div
          className="rise tv-card w-full shrink-0 px-5 py-4 sm:w-auto sm:min-w-64"
          style={{ "--i": 2 } as React.CSSProperties}
        >
          {heroi ? (
            <>
              <p className="text-[11px] font-semibold tracking-[0.16em] text-navy-mute uppercase">
                {heroi.rotulo}
              </p>
              <p className="mt-1 font-display text-3xl font-bold tabular-nums text-navy-ink sm:text-4xl">
                {heroi.valor}
              </p>
              {heroi.detalhe && (
                <p className="mt-1.5 text-xs leading-relaxed text-navy-mute">{heroi.detalhe}</p>
              )}
            </>
          ) : (
            <p className="text-sm leading-relaxed text-navy-mute">
              {vazio ?? "Preencha os campos para ver o resultado."}
            </p>
          )}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// A barra de abas
// ---------------------------------------------------------------------------

export interface AbaDef {
  /** Segmento na URL. Vazio é a aba inicial do módulo. */
  slug: string;
  rotulo: string;
}

/**
 * As abas de um módulo, como links.
 *
 * `aria-current="page"` marca a ativa para o leitor de tela — a cor sozinha não
 * comunica, e é a mesma razão de o hub não usar só cor para estado em lugar
 * nenhum.
 */
export function Abas({ base, abas }: { base: string; abas: AbaDef[] }) {
  const { pathname } = useLocation();
  const atual = pathname.replace(/\/+$/, "");
  return (
    // Sem sobreposição no painel navy. A primeira versão tinha `-mt-5`, e a barra
    // branca ficava metade dentro metade fora do hero — comia o canto arredondado
    // do painel e parecia um recorte errado. Visto na captura de 25/08.
    <nav aria-label="Seções deste módulo" className="mt-6 mb-7 overflow-x-auto px-1 pb-1">
      <ul className="surface flex min-w-max gap-1 rounded-full p-1.5">
        {abas.map((a) => {
          const para = a.slug ? `${base}/${a.slug}` : base;
          const ativa = atual === para.replace(/\/+$/, "");
          return (
            <li key={a.slug}>
              <Link
                to={para}
                aria-current={ativa ? "page" : undefined}
                className={
                  "block rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition " +
                  (ativa
                    ? "bg-azul text-navy-ink shadow-[0_6px_16px_-8px_rgb(16_40_80/0.6)]"
                    : "text-cinza hover:bg-chip hover:text-tinta")
                }
              >
                {a.rotulo}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Resultado
// ---------------------------------------------------------------------------

/**
 * Uma linha de valor.
 *
 * `tabular-nums` alinha as casas decimais na coluna, que é o que torna uma lista
 * de reais legível — sem isso os centavos dançam de linha em linha. Não é enfeite:
 * é o que faz a coluna somar visualmente.
 */
export function Linha({
  rotulo,
  valor,
  ajuda,
  tom = "normal",
}: {
  rotulo: ReactNode;
  valor: string;
  ajuda?: string;
  tom?: "normal" | "total" | "menos";
}) {
  const classe =
    tom === "total"
      ? "mt-1 border-t border-borda pt-2.5 font-bold text-titulo"
      : tom === "menos"
        ? "text-erro-tinta"
        : "text-tinta";
  return (
    <div className={`flex items-baseline justify-between gap-4 py-1.5 text-sm ${classe}`}>
      <span className="min-w-0">
        {rotulo}
        {ajuda && <span className="mt-0.5 block text-xs font-normal text-cinza">{ajuda}</span>}
      </span>
      <span className="shrink-0 font-medium tabular-nums">{valor}</span>
    </div>
  );
}

/** Um bloco de resultado, com título. */
export function Bloco({
  titulo,
  children,
  destaque,
  acao,
}: {
  titulo: string;
  children: ReactNode;
  destaque?: boolean;
  acao?: ReactNode;
}) {
  // `<section role="group" aria-labelledby>` e NÃO `<fieldset><legend>`: a
  // especificação posiciona o legend SOBRE a borda, e com o card arredondado o
  // título saía flutuando fora da caixa. Cicatriz vista na tela em 19/08.
  const id = `bloco-${titulo.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <section
      role="group"
      aria-labelledby={id}
      className={"surface p-4 sm:p-5 " + (destaque ? "border-l-4 border-l-laranja" : "")}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 id={id} className="text-[11px] font-bold tracking-[0.14em] text-titulo uppercase">
          {titulo}
        </h3>
        {acao}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// A cascata: a composição em barra proporcional
// ---------------------------------------------------------------------------

export interface FatiaCascata {
  rotulo: string;
  valor: number;
  /** A cor da fatia. Só de marca ou de estado — ver o comentário. */
  cor: "azul" | "azul-medio" | "laranja" | "erro" | "sucesso" | "cinza";
  detalhe?: string;
}

const FUNDO_FATIA: Record<FatiaCascata["cor"], string> = {
  azul: "bg-azul",
  "azul-medio": "bg-azul-medio",
  laranja: "bg-laranja",
  erro: "bg-erro-tinta",
  sucesso: "bg-sucesso",
  cinza: "bg-cinza",
};

/**
 * A composição de um total, em barra horizontal proporcional.
 *
 * É a peça que faz o aluno VER o que a lista de números só diz: que o desconto é
 * uma fatia pequena do bruto, que a periculosidade pesa mais que a hora extra, que
 * o encargo patronal é um terço da folha.
 *
 * **Nenhum texto vai DENTRO das fatias.** A legenda fica fora, em texto normal,
 * por duas razões: fatia pequena não caberia o rótulo (e cortá-lo é pior que não
 * ter), e texto sobre cor de marca exigiria conferir contraste fatia por fatia. A
 * cor identifica; a legenda nomeia.
 *
 * Fatias com valor zero não entram — barra com segmento de largura zero desenha
 * uma linha de 1px que parece defeito.
 */
export function Cascata({
  fatias,
  total,
  rotuloTotal,
}: {
  fatias: FatiaCascata[];
  total: number;
  rotuloTotal: string;
}) {
  const vivas = fatias.filter((f) => f.valor > 0);
  const soma = vivas.reduce((s, f) => s + f.valor, 0);
  if (soma <= 0) return null;

  return (
    <div>
      <div
        className="flex h-3.5 w-full overflow-hidden rounded-full bg-superficie-2"
        role="img"
        aria-label={`Composição de ${rotuloTotal}: ${vivas.map((f) => `${f.rotulo} ${((f.valor / soma) * 100).toFixed(0)}%`).join(", ")}`}
      >
        {vivas.map((f) => (
          <span
            key={f.rotulo}
            className={`${FUNDO_FATIA[f.cor]} transition-[width] duration-300`}
            style={{ width: `${(f.valor / soma) * 100}%` }}
          />
        ))}
      </div>
      <ul className="mt-3 grid gap-x-5 gap-y-1.5 sm:grid-cols-2">
        {vivas.map((f) => (
          <li key={f.rotulo} className="flex items-baseline gap-2 text-sm">
            <span
              aria-hidden="true"
              className={`mt-1.5 size-2.5 shrink-0 rounded-sm ${FUNDO_FATIA[f.cor]}`}
            />
            <span className="min-w-0 grow text-tinta">
              {f.rotulo}
              {f.detalhe && <span className="block text-xs text-cinza">{f.detalhe}</span>}
            </span>
            <span className="shrink-0 text-sm font-medium tabular-nums text-tinta">
              {((f.valor / soma) * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 border-t border-borda pt-2 text-sm font-bold text-titulo">
        {rotuloTotal}
        <span className="float-right tabular-nums">{total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A régua: a tabela progressiva como imagem
// ---------------------------------------------------------------------------

export interface DegrauRegua {
  /** Limite superior da faixa. `null` é "sem teto". */
  limite: number | null;
  aliquota: number;
  parcelaDeduzir: number;
}

/**
 * A tabela progressiva desenhada em escala, com o marcador onde o valor caiu.
 *
 * Substitui a pergunta "por que caí nesta alíquota?" por uma imagem. Cada faixa
 * ocupa a largura proporcional ao intervalo que cobre, e a opacidade cresce com a
 * alíquota — o degrau é visível antes de qualquer número ser lido.
 *
 * A faixa sem teto (`limite: null`) precisa de uma largura ARBITRÁRIA para poder
 * ser desenhada, e usa 25% do que veio antes. Não é dado: é o único jeito de
 * mostrar "e daqui para cima" sem inventar um teto que não existe. A legenda diz
 * "sem teto" com letra.
 */
export function Regua({
  degraus,
  valor,
  teto,
  rotulo,
  formatar,
}: {
  degraus: DegrauRegua[];
  /** O valor a marcar. Zero ou negativo esconde o marcador. */
  valor: number;
  /** O teto da tabela, quando há um (INSS). Marca uma linha própria. */
  teto?: number | null;
  rotulo: string;
  formatar: (v: number) => string;
}) {
  if (degraus.length === 0) return null;
  const ultimoComTeto = [...degraus].reverse().find((d) => d.limite !== null)?.limite ?? 0;
  const fim = degraus[degraus.length - 1].limite ?? ultimoComTeto * 1.25;
  if (fim <= 0) return null;

  const posicao = (v: number) => Math.min(100, Math.max(0, (v / fim) * 100));
  const marcar = valor > 0;

  return (
    <div>
      <p className="mb-2 text-[11px] font-bold tracking-[0.14em] text-titulo uppercase">{rotulo}</p>
      <div className="relative pt-6 pb-1">
        {/* O marcador do valor, acima do trilho. */}
        {marcar && (
          <div
            className="absolute top-0 -translate-x-1/2 transition-[left] duration-300"
            style={{ left: `${posicao(Math.min(valor, fim))}%` }}
          >
            <span className="block rounded bg-laranja px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap text-azul">
              {formatar(valor)}
            </span>
            <span
              aria-hidden="true"
              className="mx-auto block size-0 border-x-4 border-t-4 border-x-transparent border-t-laranja"
            />
          </div>
        )}

        <div className="flex h-7 w-full overflow-hidden rounded-cf border border-borda">
          {degraus.map((d, i) => {
            const inicio = i === 0 ? 0 : (degraus[i - 1].limite ?? 0);
            const termina = d.limite ?? fim;
            const largura = ((termina - inicio) / fim) * 100;
            // A opacidade cresce com a alíquota: o degrau fica visível antes de
            // qualquer número ser lido. Escala relativa à maior alíquota da
            // tabela, para funcionar com qualquer tabela que o banco devolver.
            const maior = Math.max(...degraus.map((x) => x.aliquota)) || 1;
            const forca = 0.25 + 0.75 * (d.aliquota / maior);
            return (
              <span
                key={i}
                className="grid place-items-center border-r border-superficie/40 bg-azul-medio text-[10px] font-bold text-navy-ink last:border-r-0"
                style={{ width: `${largura}%`, opacity: forca }}
                title={`até ${d.limite === null ? "sem teto" : formatar(d.limite)} — ${(d.aliquota * 100).toLocaleString("pt-BR")}%`}
              >
                {largura > 8 ? `${(d.aliquota * 100).toLocaleString("pt-BR")}%` : ""}
              </span>
            );
          })}
        </div>

        <div className="mt-1 flex justify-between text-[11px] tabular-nums text-cinza">
          <span>{formatar(0)}</span>
          {teto ? (
            <span>
              teto <b className="font-semibold text-tinta">{formatar(teto)}</b>
            </span>
          ) : (
            <span>{degraus[degraus.length - 1].limite === null ? "sem teto" : formatar(fim)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// O comparador de duas opções
// ---------------------------------------------------------------------------

/**
 * Duas opções lado a lado, com a vencedora marcada.
 *
 * Substitui duas linhas de lista ("Modelo 1 … / Modelo 2 …") por uma comparação
 * que se lê de um olhar. A vencedora leva borda laranja e selo — cor E texto, e
 * não só cor: quem não distingue laranja precisa do selo.
 */
export function Comparador({
  opcoes,
  aoEscolher,
  empate,
}: {
  opcoes: Array<{
    chave: string;
    titulo: string;
    valor: string;
    detalhe?: string;
    vencedora: boolean;
    escolhida: boolean;
  }>;
  aoEscolher?: (chave: string) => void;
  /**
   * Quando os dois dão o MESMO resultado, o selo "mais vantajoso" mente.
   *
   * Acontece de verdade e não é caso de borda: com a redução do IR de 2026, todo
   * salário até cerca de R$ 4.000 zera o imposto nos dois modelos. Coroar um
   * deles ali ensinaria uma escolha que não existe — o texto do empate explica
   * por que os dois deram zero, que é a informação útil.
   */
  empate?: string;
}) {
  if (empate) {
    return (
      <div>
        <div className="grid gap-3 sm:grid-cols-2">
          {opcoes.map((o) => (
            <div key={o.chave} className="rounded-cf border border-borda bg-superficie-2 p-3.5">
              <span className="text-xs font-semibold text-tinta">{o.titulo}</span>
              <p className="mt-1 font-display text-xl font-bold tabular-nums text-titulo">{o.valor}</p>
              {o.detalhe && <p className="mt-0.5 text-xs leading-relaxed text-cinza">{o.detalhe}</p>}
            </div>
          ))}
        </div>
        <p className="mt-3 rounded-cf bg-aviso-fundo px-3 py-2 text-xs leading-relaxed text-aviso-tinta">
          <strong>Os dois dão o mesmo resultado.</strong> {empate}
        </p>
      </div>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {opcoes.map((o) => {
        const Elemento = aoEscolher ? "button" : "div";
        return (
          <Elemento
            key={o.chave}
            {...(aoEscolher
              ? { type: "button" as const, onClick: () => aoEscolher(o.chave), "aria-pressed": o.escolhida }
              : {})}
            className={
              "rounded-cf border p-3.5 text-left transition " +
              (o.vencedora ? "border-laranja bg-alerta-fundo" : "border-borda bg-superficie-2") +
              (aoEscolher ? " hover:border-laranja" : "") +
              (o.escolhida ? " ring-2 ring-laranja/40" : "")
            }
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-tinta">{o.titulo}</span>
              {o.vencedora && (
                <span className="rounded bg-laranja px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap text-azul uppercase">
                  mais vantajoso
                </span>
              )}
            </div>
            <p className="mt-1 font-display text-xl font-bold tabular-nums text-titulo">{o.valor}</p>
            {o.detalhe && <p className="mt-0.5 text-xs leading-relaxed text-cinza">{o.detalhe}</p>}
            {o.escolhida && (
              <p className="mt-1.5 text-[11px] font-semibold text-laranja-texto">em uso no cálculo</p>
            )}
          </Elemento>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// O contracheque: duas colunas, como o papel de verdade
// ---------------------------------------------------------------------------

export interface ItemContracheque {
  rotulo: string;
  valor: string;
  ajuda?: string;
}

/**
 * Proventos e descontos em DUAS COLUNAS, com o líquido em faixa própria.
 *
 * É a forma que o contador reconhece de imediato — todo holerite do país se
 * apresenta assim (Portaria 3.626/91). A primeira versão empilhava os dois em
 * caixas separadas, o que é legível e não é reconhecível: perdia a leitura
 * horizontal "o que entra × o que sai" que faz o documento ser lido em segundos.
 *
 * Abaixo de 640px as colunas empilham, porque duas colunas de moeda em 360px
 * quebram os centavos de linha.
 */
export function Contracheque({
  proventos,
  descontos,
  totalProventos,
  totalDescontos,
  liquido,
  rodape,
}: {
  proventos: ItemContracheque[];
  descontos: ItemContracheque[];
  totalProventos: string;
  totalDescontos: string;
  liquido: string;
  rodape?: ReactNode;
}) {
  return (
    <section className="surface overflow-hidden">
      <div className="grid sm:grid-cols-2">
        <Coluna coluna="proventos" titulo="Proventos" itens={proventos} total={totalProventos} />
        <Coluna
          coluna="descontos"
          titulo="Descontos"
          itens={descontos}
          total={totalDescontos}
          vazio="Nenhum desconto nesta competência."
          borda
        />
      </div>
      <div
        data-linha="liquido"
        className="flex flex-wrap items-baseline justify-between gap-2 bg-azul px-4 py-3.5 sm:px-5"
      >
        <span className="text-[11px] font-bold tracking-[0.14em] text-navy-mute uppercase">
          Líquido a receber
        </span>
        <span className="font-display text-2xl font-bold tabular-nums text-navy-ink sm:text-3xl">
          {liquido}
        </span>
      </div>
      {rodape && <div className="border-t border-borda px-4 py-3 sm:px-5">{rodape}</div>}
    </section>
  );
}

/**
 * Uma coluna do contracheque.
 *
 * `data-coluna` existe para a prova de tela: as duas colunas têm uma linha
 * chamada "Total", e sem escopo nomeado o seletor por texto pegaria a primeira
 * que encontrasse — é a cicatriz das sete etiquetas iguais, agora prevenida no
 * componente em vez de no teste.
 */
function Coluna({
  coluna,
  titulo,
  itens,
  total,
  vazio,
  borda,
}: {
  coluna: string;
  titulo: string;
  itens: ItemContracheque[];
  total: string;
  vazio?: string;
  borda?: boolean;
}) {
  return (
    <div
      data-coluna={coluna}
      className={"p-4 sm:p-5 " + (borda ? "border-t border-borda sm:border-t-0 sm:border-l" : "")}
    >
      <h3 className="mb-2 text-[11px] font-bold tracking-[0.14em] text-titulo uppercase">{titulo}</h3>
      {itens.length === 0 ? (
        <p className="py-1.5 text-sm text-cinza">{vazio ?? "—"}</p>
      ) : (
        itens.map((i) => (
          <div key={i.rotulo} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
            <span className="min-w-0 text-tinta">
              {i.rotulo}
              {i.ajuda && <span className="block text-xs text-cinza">{i.ajuda}</span>}
            </span>
            <span className="shrink-0 font-medium tabular-nums text-tinta">{i.valor}</span>
          </div>
        ))
      )}
      <p className="mt-1 flex items-baseline justify-between gap-3 border-t border-borda pt-2.5 text-sm font-bold text-titulo">
        <span>Total</span>
        <span className="tabular-nums">{total}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alerta
// ---------------------------------------------------------------------------

export type TomAlerta = "informacao" | "atencao" | "erro" | "acerto";

/**
 * Aviso colorido, nos quatro tons.
 *
 * Os quatro têm PARES de token no hub (fundo e tinta), e é isso que os faz
 * sobreviver ao tema escuro. Na origem `warning` e `info` eram idênticos — mesmo
 * fundo navy, mesma borda laranja, diferindo só no ícone —, o que fazia o aluno
 * não distinguir "cuidado" de "para saber".
 */
export function Alerta({
  tom = "informacao",
  titulo,
  children,
}: {
  tom?: TomAlerta;
  titulo?: string;
  children: ReactNode;
}) {
  const cores: Record<TomAlerta, string> = {
    informacao: "bg-chip text-tinta border-azul-medio",
    atencao: "bg-alerta-fundo text-alerta-tinta border-laranja",
    erro: "bg-erro-fundo text-erro-tinta border-erro-tinta",
    acerto: "bg-aviso-fundo text-aviso-tinta border-aviso-tinta",
  };
  return (
    <div className={`rounded-cf border-l-4 px-4 py-3 text-sm leading-relaxed ${cores[tom]}`}>
      {titulo && <strong className="block">{titulo}</strong>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabelas de referência
// ---------------------------------------------------------------------------

export interface TabelaRef {
  titulo: string;
  colunas: string[];
  linhas: string[][];
  /** Nota de pé, quando a tabela precisa de uma. */
  nota?: string;
}

/**
 * Uma tabela de referência.
 *
 * O container tem `overflow-x-auto` porque tabela de quatro colunas não cabe em
 * 360px — e a regra da casa é que conteúdo largo rola dentro do próprio
 * container, nunca fazendo a página rolar de lado.
 */
export function Tabela({ tabela }: { tabela: TabelaRef }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-bold text-titulo">{tabela.titulo}</h3>
      <div className="surface overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr className="bg-superficie-2">
              {tabela.colunas.map((c) => (
                <th
                  key={c}
                  scope="col"
                  className="px-3 py-2.5 text-left text-[11px] font-semibold tracking-[0.1em] text-cinza uppercase"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabela.linhas.map((l, i) => (
              <tr key={i} className={i % 2 === 1 ? "bg-superficie-2/50" : undefined}>
                {l.map((celula, j) => (
                  <td key={j} className="border-t border-borda px-3 py-2.5 tabular-nums text-tinta">
                    {celula}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tabela.nota && <p className="mt-1.5 text-xs leading-relaxed text-cinza">{tabela.nota}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documentos
// ---------------------------------------------------------------------------

/**
 * Abre um documento HTML numa aba nova, para o aluno imprimir ou salvar em PDF.
 *
 * `document.write` num `window.open` e NÃO um blob com `<a download>`: o
 * visualizador bloqueia download iniciado pela própria página, e um `<a download>`
 * seria um link morto. Além disso, a aba aberta com `noopener` não recebe
 * `window.opener`, então o documento não alcança a sessão do hub.
 *
 * Devolve `false` quando o bloqueador de pop-up impediu — a tela precisa dizer
 * isso, senão o aluno clica e nada acontece.
 */
export function abrirDocumento(html: string): boolean {
  const janela = window.open("", "_blank", "noopener,noreferrer");
  if (!janela) return false;
  janela.document.open();
  janela.document.write(html);
  janela.document.close();
  return true;
}

/** Copia texto, devolvendo se deu certo. Falha de permissão não estoura. */
export async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    // `navigator.clipboard` exige contexto seguro e pode ser negado por
    // permissão. A origem não tinha try/catch: a promessa rejeitava em silêncio e
    // o "Copiado ✓" nem aparecia, deixando o aluno sem saber se funcionou.
    return false;
  }
}

/** O botão de gerar documento, com o aviso de pop-up bloqueado. */
export function BotaoDocumento({
  rotulo,
  aoClicar,
  bloqueado,
}: {
  rotulo: string;
  aoClicar: () => void;
  bloqueado: boolean;
}) {
  return (
    <div>
      <button type="button" onClick={aoClicar} className="pill-cta">
        {rotulo}
      </button>
      {bloqueado && (
        <p className="mt-2 text-xs leading-relaxed text-erro-tinta">
          O navegador bloqueou a abertura da nova aba. Libere os pop-ups deste site e clique de novo.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Casos práticos
// ---------------------------------------------------------------------------

export interface CasoPratico<T> {
  nome: string;
  descricao: string;
  valores: T;
}

/**
 * Os casos práticos de um módulo, como cartões que carregam a calculadora.
 *
 * Cada caso navega para a aba da calculadora e injeta os valores — em vez de
 * mostrar o resultado ali. É o desenho da origem, e é o certo: o aluno vê os
 * campos preenchidos e pode mexer, que é onde se aprende.
 */
export function Casos<T>({
  casos,
  aoCarregar,
}: {
  casos: CasoPratico<T>[];
  aoCarregar: (valores: T) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {casos.map((c, i) => (
        <article
          key={c.nome}
          className="rise surface flex flex-col p-4"
          style={{ "--i": Math.min(i, 5) } as React.CSSProperties}
        >
          <h3 className="text-sm font-bold text-titulo">{c.nome}</h3>
          <p className="mt-1 grow text-xs leading-relaxed text-cinza">{c.descricao}</p>
          <button
            type="button"
            onClick={() => aoCarregar(c.valores)}
            className="btn-contorno mt-3 self-start text-xs"
          >
            Carregar na calculadora
          </button>
        </article>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estado da ferramenta
// ---------------------------------------------------------------------------

/**
 * O rodapé de cada módulo: de que ano são as tabelas, e o aviso educacional.
 *
 * A referência vem do BANCO (`dp_parametros_folha`), não de um texto cravado. É a
 * defesa contra a ferramenta ensinar valor velho como atual — o defeito medido na
 * origem, onde o INSS era de 2026 e o IRRF de 2025 no mesmo arquivo, e nada na
 * tela dizia isso.
 */
export function Referencias({ inss, irrf }: { inss: string; irrf: string }) {
  return (
    <footer className="mt-9 flex flex-wrap items-start gap-x-8 gap-y-3 rounded-cf bg-superficie-2 px-4 py-3.5 text-xs leading-relaxed text-cinza">
      <p>
        <span className="block text-[10px] font-bold tracking-[0.14em] text-titulo uppercase">
          Tabelas aplicadas
        </span>
        INSS — {inss || "não informado"}
        <br />
        IRRF — {irrf || "não informado"}
      </p>
      <p className="max-w-md">
        <span className="block text-[10px] font-bold tracking-[0.14em] text-titulo uppercase">
          Ferramenta educacional
        </span>
        Os cálculos seguem a legislação nas versões indicadas e não substituem a conferência de um
        profissional. Nada do que você digita é gravado.
      </p>
    </footer>
  );
}

/** A tela de espera enquanto os parâmetros chegam do banco. */
export function Carregando() {
  return (
    <p className="surface p-6 text-sm text-cinza">Carregando as tabelas vigentes…</p>
  );
}

/**
 * A tela de quando os parâmetros não vieram.
 *
 * Uma mensagem só para os dois motivos possíveis (sem permissão, ou fora do ar),
 * porque quem chega aqui sem o card veio pela URL digitada à mão e não há nada de
 * útil a dizer sobre a diferença. É a mesma decisão de `lerParametrosDas`.
 */
export function SemParametros() {
  return (
    <Alerta tom="erro" titulo="As tabelas não carregaram">
      Não foi possível ler as tabelas de INSS e IRRF vigentes. Recarregue a página; se continuar,
      fale com o suporte pelo menu da sua conta.
    </Alerta>
  );
}
